import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Dealership from '../../../../types/dealership/Dealership';
import DealershipWriteDTO from '../../../../types/dealership/DealershipWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { DealershipService } from '../../services/dealership.service';
import { ExportService } from '../../services/export.service';
import {
    CreateDealershipDialogComponent
} from '../shared/dealerships/create-dealership-dialog/create-dealership-dialog.component';
import {
    UpdateDealershipDialogComponent
} from '../shared/dealerships/update-dealership-dialog/update-dealership-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-dealerships',
    templateUrl: './dealerships.component.html',
    styleUrls: [ './dealerships.component.scss' ],
    imports: [
        FormsModule,
        TableModule,
        RouterLink,
        PageHeaderComponent,
        IftaLabelModule,
        IconField,
        InputIcon,
        InputText,
        Button,
        CreateDealershipDialogComponent,
        UpdateDealershipDialogComponent,
        IsAdminDirective,
        Tooltip
    ]
})
export class DealershipsComponent implements OnInit {
    public search = '';
    public dealerships: Dealership[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateDialog = false;
    public displayUpdateDialog = false;
    public selectedDealership: DealershipWriteDTO = new DealershipWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(
        private dealershipService: DealershipService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private exportService: ExportService
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadDealerships();

        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0;
            this.loadDealerships();
        });
    }

    /**
     * Handles the change of the page. Reloads the dealerships based on the new page.
     *
     * @param event The event containing the page information.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadDealerships();
    }

    /**
     * Handles the change of the search input. Emits the new search value.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Handles the creation of a new dealership.
     */
    public editDealership(dealership: Dealership): void {
        this.selectedDealership = new DealershipWriteDTO(dealership);
        this.selectedDealership.countryId = dealership?.country?.id;
        this.displayUpdateDialog = true;
    }

    /**
     * Deletes the selected dealership after confirmation.
     *
     * @param dealership The dealership to delete.
     */
    public deleteDealership(dealership: Dealership): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the dealership ' + dealership.name + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.dealershipService.deleteDealership(dealership.id).then(() => {
                    this.loadDealerships();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Dealership deleted',
                        life: 3000,
                    });
                }).catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
            },
        });
    }

    /**
     * Opens the dialog to create a new dealership.
     */
    public openCreateDialog(): void {
        this.displayCreateDialog = true;
    }

    /**
     * Reloads the dealerships.
     */
    public reloadDealerships(): void {
        this.loadDealerships();
    }

    /**
     * Exports the dealerships to a file.
     */
    public exportDealerships(): void {
        this.exportService.exportDealerships().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Dealerships exported successfully',
                life: 3000,
            });
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }

    /**
     * Loads the dealerships from the service.
     */
    private loadDealerships(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.dealershipService
            .getDealerships(search, this.currentPage, size)
            .then((result: Page<Dealership>) => {
                this.dealerships = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
